import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Star,
  Heart,
  ArrowRight,
  Spinner,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackForm = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    wants_contact_exchange: null,
    wants_another_match: null,
    feedback_text: '',
    report_type: 'no_issue'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (formData.rating === 0) {
      toast.error('Please rate your experience');
      return;
    }
    if (formData.wants_contact_exchange === null) {
      toast.error('Please indicate if you want to exchange contact info');
      return;
    }
    if (formData.wants_another_match === null) {
      toast.error('Please indicate if you want another match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        match_id: matchId,
        ...formData
      });
      
      if (formData.wants_another_match) {
        updateUser({ application_status: 'ready_for_matching' });
      }
      
      toast.success('Feedback submitted! Thank you.');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit feedback';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Heart weight="light" className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="font-serif text-3xl md:text-4xl mb-4">
              How Was Your Call?
            </h1>
            <p className="text-muted-foreground">
              Your feedback helps us improve our matchmaking and keeps our community safe.
            </p>
          </div>

          <div className="paper-card rounded-sm p-8 space-y-10">
            {/* Rating */}
            <div className="space-y-4">
              <Label className="text-base">How was your call experience?</Label>
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleChange('rating', star)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star
                      weight={formData.rating >= star ? 'fill' : 'regular'}
                      className={`w-10 h-10 ${
                        formData.rating >= star ? 'text-amber-400' : 'text-muted-foreground/30'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {formData.rating === 0 && 'Click to rate'}
                {formData.rating === 1 && 'Poor'}
                {formData.rating === 2 && 'Fair'}
                {formData.rating === 3 && 'Good'}
                {formData.rating === 4 && 'Great'}
                {formData.rating === 5 && 'Excellent!'}
              </p>
            </div>

            {/* Contact Exchange */}
            <div className="space-y-4">
              <Label className="text-base">
                Would you like to exchange contact information with this person?
              </Label>
              <p className="text-sm text-muted-foreground">
                Contact info will only be shared if both parties agree.
              </p>
              <RadioGroup
                value={formData.wants_contact_exchange === null ? '' : formData.wants_contact_exchange.toString()}
                onValueChange={(value) => handleChange('wants_contact_exchange', value === 'true')}
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="true" id="contact-yes" data-testid="contact-yes" />
                  <Label htmlFor="contact-yes" className="cursor-pointer font-normal">
                    Yes, I'd like to stay in touch
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="false" id="contact-no" data-testid="contact-no" />
                  <Label htmlFor="contact-no" className="cursor-pointer font-normal">
                    No, thank you
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Another Match */}
            <div className="space-y-4">
              <Label className="text-base">Would you like another match?</Label>
              <RadioGroup
                value={formData.wants_another_match === null ? '' : formData.wants_another_match.toString()}
                onValueChange={(value) => handleChange('wants_another_match', value === 'true')}
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="true" id="match-yes" data-testid="match-yes" />
                  <Label htmlFor="match-yes" className="cursor-pointer font-normal">
                    Yes, keep searching for me
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="false" id="match-no" data-testid="match-no" />
                  <Label htmlFor="match-no" className="cursor-pointer font-normal">
                    No, I'm taking a break
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Feedback Text */}
            <div className="space-y-2">
              <Label htmlFor="feedback_text">
                Any feedback for our team? (Optional)
              </Label>
              <Textarea
                id="feedback_text"
                placeholder="Help us improve our matchmaking..."
                value={formData.feedback_text}
                onChange={(e) => handleChange('feedback_text', e.target.value)}
                rows={4}
                data-testid="feedback-text-textarea"
              />
            </div>

            {/* Report */}
            <div className="space-y-4 p-4 bg-muted/50 rounded-sm">
              <div className="flex items-center gap-2">
                <Warning weight="fill" className="w-5 h-5 text-amber-600" />
                <Label className="text-base">Report an Issue (Optional)</Label>
              </div>
              <Select
                value={formData.report_type}
                onValueChange={(value) => handleChange('report_type', value)}
              >
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_issue">No issues to report</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate behavior</SelectItem>
                  <SelectItem value="unsafe">Felt unsafe</SelectItem>
                  <SelectItem value="fake_profile">Suspected fake profile</SelectItem>
                  <SelectItem value="other">Other concern</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Reports are taken seriously and reviewed by our moderation team.
              </p>
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-primary w-full py-6"
              data-testid="submit-feedback-btn"
            >
              {loading ? (
                <Spinner className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Submit Feedback
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default FeedbackForm;
